import React from 'react'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'

import {
  Button,
  BUTTON_VARIANT_SECONDARY,
  MessageBox,
  Modal,
  ModalHead,
  ModalBody,
  ModalFoot,
  RadioButtonsGroup,
  SingleLoader,
  Flex,
  FlexItem,
  Text,
  TEXT_VARIANT_ITALIC
} from 'mailup-web-components'

import SegmentationPanel from './components/SegmentationPanel'
import SegmentationInfo from './components/SegmentationInfo'

const ModalSendingSegmentation = ({
  show,
  error,
  severity,
  loading,
  groups,
  excludedGroups,
  filters,
  section,
  setModalState,
  closeModal,
  queued,
  simulationEnabled,
  simulationLoading,
  simulationLoaded,
  simulationResult,
  simulationDiff,
  handleSimulate
}) => {
  const intl = useIntl()

  const handleSelectorChange = newValue => {
    setModalState(prev => ({
      ...prev,
      section: newValue
    }))
  }

  const noData =
    groups.length === 0 && excludedGroups.length === 0 && filters.length === 0

  const intlGroups = groups.map(group => ({
    ...group,
    name:
      group.id === -1
        ? intl.formatMessage({ id: 'Load.Filters.NoGroupOption' })
        : group.deleted
        ? intl.formatMessage(
            { id: 'Load.ModalSegmentation.GroupDeleted' },
            { x: group.id }
          )
        : `ID ${group.id} - ${group.name}`
  }))

  const intlExcludedGroups = excludedGroups.map(group => ({
    ...group,
    name: group.deleted
      ? intl.formatMessage(
          { id: 'Load.ModalSegmentation.GroupDeleted' },
          { x: group.id }
        )
      : `ID ${group.id} - ${group.name}`
  }))

  const intlFilters = filters.map(filter => ({
    ...filter,
    name: filter.deleted
      ? intl.formatMessage(
          { id: 'Load.ModalSegmentation.FilterDeleted' },
          { x: filter.id }
        )
      : `ID ${filter.id} - ${filter.name}`
  }))

  const selectorItems = [
    {
      value: 'groups',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Statistics.IncludedGroups' })} (
          {intl.formatNumber(groups.length)})
        </>
      ),
      disabled: !groups.length,
      dataAttributeTest: 'modalSegmentation-includedGroups-click'
    },
    {
      value: 'excludedGroups',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Statistics.ExcludedGroups' })} (
          {intl.formatNumber(excludedGroups.length)})
        </>
      ),
      disabled: !excludedGroups.length,
      dataAttributeTest: 'modalSegmentation-excludedGroups-click'
    },
    {
      value: 'filters',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Common.AppliedFlters' })} (
          {intl.formatNumber(filters.length)})
        </>
      ),
      disabled: !filters.length,
      dataAttributeTest: 'modalSegmentation-appliedFlters-click'
    }
  ]

  return (
    <Modal
      show={show}
      showClose
      data-mu-test='modal-sending-segmentation'
      onClose={closeModal}
    >
      <ModalHead
        title={intl.formatMessage({ id: 'Load.ModalSegmentation.Title' })}
      />
      {error && <MessageBox variant={severity}>{error}</MessageBox>}

      {!error && (
        <>
          {loading && (
            <>
              <SingleLoader height='225px' />
            </>
          )}

          {!loading && (
            <>
              <ModalBody scrollable>
                <Flex flexDirection='column' maxHeight='400px' rowGap='24px'>
                  {noData && (
                    <Flex
                      height='225px'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <Text variant={TEXT_VARIANT_ITALIC}>
                        {intl.formatMessage({
                          id: 'Load.Common.RecipientSelectNoData'
                        })}
                      </Text>
                    </Flex>
                  )}
                  {!noData && (
                    <>
                      <FlexItem margin='0 0 16px 0'>
                        <SegmentationInfo
                          queued={queued}
                          simulationEnabled={simulationEnabled}
                          simulationLoading={simulationLoading}
                          simulationLoaded={simulationLoaded}
                          simulationResult={simulationResult}
                          simulationDiff={simulationDiff}
                          handleSimulate={handleSimulate}
                        />
                      </FlexItem>
                      <Flex justifyContent='center'>
                        <RadioButtonsGroup
                          value={section}
                          items={selectorItems}
                          onChange={handleSelectorChange}
                        />
                      </Flex>

                      <div>
                        {section === 'groups' && (
                          <SegmentationPanel
                            title={intl.formatMessage({
                              id: 'Load.Statistics.IncludedGroups'
                            })}
                            items={intlGroups}
                          />
                        )}

                        {section === 'excludedGroups' && (
                          <SegmentationPanel
                            title={intl.formatMessage({
                              id: 'Load.Statistics.ExcludedGroups'
                            })}
                            items={intlExcludedGroups}
                          />
                        )}

                        {section === 'filters' && (
                          <SegmentationPanel
                            title={intl.formatMessage({
                              id: 'Load.Common.AppliedFlters'
                            })}
                            items={intlFilters}
                          />
                        )}
                      </div>
                    </>
                  )}
                </Flex>
              </ModalBody>

              <ModalFoot>
                <Button
                  data-mu-test='modalSegmentation-closeBtn-click'
                  kind={BUTTON_VARIANT_SECONDARY}
                  onClick={closeModal}
                >
                  {intl.formatMessage({ id: 'Load.Common.Close' })}
                </Button>
              </ModalFoot>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default ModalSendingSegmentation

ModalSendingSegmentation.propTypes = {
  show: PropTypes.bool,
  error: PropTypes.any,
  severity: PropTypes.string,
  loading: PropTypes.bool,
  groups: PropTypes.array,
  excludedGroups: PropTypes.array,
  filters: PropTypes.array,
  section: PropTypes.any,
  setModalState: PropTypes.func,
  closeModal: PropTypes.func,
  queued: PropTypes.any,
  simulationEnabled: PropTypes.bool,
  simulationLoading: PropTypes.bool,
  simulationLoaded: PropTypes.bool,
  simulationResult: PropTypes.any,
  simulationDiff: PropTypes.any,
  handleSimulate: PropTypes.func
}
